/**
 * @fileoverview Mock backend for the unified sales-user roster.
 * @description Registers GET /sales/sales-users (list, with filters), GET /sales/sales-users/:id
 * (the shared "View User" profile — the same payload User Management and Sales Team are both meant
 * to open, per "View User opens the same read-only profile from User Management and Sales Team")
 * and PATCH /sales/sales-users/:id (update), on the feature's shared mock adapter (see
 * sales-mock-adapter.ts). Deactivate and Reactivate are not here: User Management does them through
 * POST /users/{userId}/deactivate and /reactivate; user-lifecycle-sync.ts applies those to this
 * roster.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { salesMockAdapter } from './sales-mock-adapter'
import { findSalesUserById, listSalesRoster, updateSalesUser } from './sales-roster-mock-store'
import { listTenants } from './tenants-mock-store'

import './user-lifecycle-sync'

import type {
  SalesUserProfile,
  UpdateSalesUserInput,
} from '@/features/sales/types/sales-roster.types'
import type { SalesUserRecord } from '@/features/sales/types/tenant.types'

if (salesMockAdapter) {
  salesMockAdapter.onGet('/sales/sales-users').reply((config) => {
    const params = (config.params ?? {}) as Partial<Pick<SalesUserRecord, 'role' | 'status'>> & {
      reportsTo?: string
    }
    const roster = listSalesRoster().filter((user) => {
      if (params.role && user.role !== params.role) return false
      if (params.status && user.status !== params.status) return false
      if (params.reportsTo && user.reportsTo !== params.reportsTo) return false
      return true
    })
    return [200, roster]
  })

  salesMockAdapter.onGet(/^\/sales\/sales-users\/[^/]+$/).reply((config) => {
    const id = config.url?.split('/').at(-1)
    const user = id ? findSalesUserById(id) : undefined

    if (!user) {
      return [404, { appCode: '1404', message: 'Sales user not found.' }]
    }

    const response: SalesUserProfile = {
      ...user,
      assignedTenantIds: listTenants({ ownerId: user.id }).map((tenant) => tenant.id),
    }
    return [200, response]
  })

  salesMockAdapter.onPatch(/^\/sales\/sales-users\/[^/]+$/).reply((config) => {
    const id = config.url?.split('/').at(-1)
    const { actingUserId, actingUserRole, ...input } = JSON.parse(config.data as string) as {
      actingUserId: string
      actingUserRole: string
    } & UpdateSalesUserInput
    const result = id ? updateSalesUser(id, input, actingUserId, actingUserRole) : null

    if (!result) {
      return [404, { appCode: '1404', message: 'Sales user not found.' }]
    }
    if ('error' in result) {
      return [422, { appCode: '1422', message: result.error }]
    }
    return [200, result]
  })
}
